/**
 * VM-M6-6: My Recent Receipts dashboard widget.
 *
 * Personal-zone widget that shows submitter's last 5 expenses and
 * highlights anything in `info_requested` (accountant blocked on user).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Receipt } from 'lucide-react';
import { ApiError } from '@/lib/api';
import type { WidgetProps } from '@/types';
import { listMyExpenses, money, RECONCILIATION_LABEL, type Expense } from '../api/vmM6Api';

export function MyRecentReceiptsWidget(_props: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Expense[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyExpenses({ limit: 5 })
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) { setRows([]); return; }
        setError(err instanceof Error ? err.message : 'Failed to load');
      });
    return () => { cancelled = true; };
  }, []);

  const infoCount = (rows ?? []).filter(
    (r) => r.reconciliation_status === 'info_requested',
  ).length;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      gap: 8, padding: 4,
    }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Receipt size={16} />
        <strong style={{ fontSize: '0.95em' }}>My Recent Receipts</strong>
        <button
          type="button"
          className="btn btn--text"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/modules/vm/expenses/new')}
        >
          <Plus size={12} /> New
        </button>
      </header>

      {error && <div style={{ fontSize: 12, color: 'var(--color-danger-dark)' }}>{error}</div>}

      {infoCount > 0 && (
        <button
          type="button"
          onClick={() => navigate('/modules/vm/expenses/mine')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 8px', borderRadius: 6,
            border: '1px solid var(--color-border)',
            background: 'var(--color-warning-light)',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <AlertTriangle size={14} />
          <span style={{ fontSize: 12 }}>
            {infoCount} need{infoCount === 1 ? 's' : ''} a response.
          </span>
        </button>
      )}

      {rows === null && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Loading…</div>
      )}

      {rows !== null && rows.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          No receipts yet — submit your first one.
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1, overflowY: 'auto' }}>
          {rows.map((e) => (
            <li
              key={e.id}
              onClick={() => navigate(`/modules/vm/expenses/${e.id}`)}
              style={{
                display: 'flex', justifyContent: 'space-between',
                gap: 8, padding: '6px 4px',
                borderBottom: '1px solid var(--color-border)',
                cursor: 'pointer', fontSize: 12,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontWeight: 500,
                }}>
                  {e.merchant_name || e.invoice_number || e.description || '(untitled)'}
                </div>
                <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
                  {e.transaction_date} · {RECONCILIATION_LABEL[e.reconciliation_status]}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontWeight: 500 }}>
                {money(e.amount_cents, e.currency)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
